import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, Gift, Prohibit, ShieldStar, Users, Copy, CheckCircle, Code, LinkSimple, Envelope, Trash, PaperPlaneTilt, ArrowUUpLeft } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import ConnectAggregator from "@/components/ConnectAggregator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useConfig, refSlug } from "@/lib/useConfig";

const Badge = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${ok ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
    <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-slate-400"}`} />{label}
  </span>
);

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState("members");
  const [inboxUnread, setInboxUnread] = useState(0);

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/dashboard", { replace: true }); }
  }, [user, navigate]);

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/admin/contacts");
      setInboxUnread(data.unread || 0);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadUnread(); }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setUsers([]);
    }
  };
  useEffect(() => { load(); }, []);

  const act = async (u, action) => {
    setBusyId(u.user_id);
    try {
      await api.post(`/admin/users/${u.user_id}/${action}`);
      toast.success(action === "grant" ? `Free access granted to ${u.email}` : `Access revoked for ${u.email}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const filtered = (users || []).filter((u) =>
    !search || u.email.toLowerCase().includes(search.toLowerCase()) || (u.name || "").toLowerCase().includes(search.toLowerCase()));

  const activeCount = (users || []).filter((u) => u.subscription_status === "active").length;
  const compedCount = (users || []).filter((u) => u.comped).length;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-2 text-[#0055FF]"><ShieldStar weight="duotone" size={20} />
          <p className="text-xs font-bold uppercase tracking-[0.14em]">Admin</p></div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 mt-2">Admin console</h1>
        <p className="text-base text-slate-600 mt-2">Manage members and read messages from your contact form.</p>

        <div className="mt-6 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="admin-tabs">
          <button onClick={() => setTab("members")} data-testid="tab-members"
            className={`px-4 h-9 rounded-md text-sm font-semibold transition-colors ${tab === "members" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Users size={15} className="inline mr-1.5 -mt-0.5" /> Members
          </button>
          <button onClick={() => setTab("inbox")} data-testid="tab-inbox"
            className={`px-4 h-9 rounded-md text-sm font-semibold transition-colors relative ${tab === "inbox" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Envelope size={15} className="inline mr-1.5 -mt-0.5" /> Inbox
            {inboxUnread > 0 && (
              <span data-testid="inbox-unread-badge" className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                {inboxUnread > 9 ? "9+" : inboxUnread}
              </span>
            )}
          </button>
        </div>

        {tab === "members" && (
        <div data-testid="members-panel">
        <PromoteCard />
        <ConnectAggregator />

        <div className="grid grid-cols-3 gap-4 mt-8 max-w-xl">
          <Stat icon={Users} label="Total" value={users?.length ?? "—"} />
          <Stat icon={ShieldStar} label="Active" value={activeCount} accent="text-emerald-600" />
          <Stat icon={Gift} label="Comped" value={compedCount} accent="text-[#0055FF]" />
        </div>

        <div className="relative mt-8 max-w-sm">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="admin-user-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…" className="pl-10 h-10" />
        </div>

        <div className="mt-5 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.5fr_1fr_0.8fr_0.6fr_200px] gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200">
            {["User", "Status", "Login", "Sites", "Access"].map((h) => (
              <span key={h} className={`text-xs font-bold uppercase tracking-[0.1em] text-slate-500 ${h === "Access" ? "text-right" : ""}`}>{h}</span>
            ))}
          </div>
          {users === null ? (
            <div className="py-16 text-center text-slate-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-500">No users found.</div>
          ) : filtered.map((u) => (
            <div key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}
              className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_0.8fr_0.6fr_200px] gap-3 md:gap-4 px-6 py-4 border-b border-slate-100 last:border-0 items-center hover:bg-slate-50/60 transition-colors">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 truncate">{u.name || "—"} {u.role === "admin" && <span className="text-[10px] font-bold uppercase text-[#0055FF] ml-1">Admin</span>}</p>
                <p className="text-xs text-slate-400 truncate">{u.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge ok={u.subscription_status === "active"} label={u.subscription_status === "active" ? "Active" : "Inactive"} />
                {u.comped && <span className="text-[10px] font-bold uppercase text-[#0055FF] bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5">Comped</span>}
              </div>
              <span className="text-sm text-slate-500 capitalize">{u.auth_provider}</span>
              <span className="text-sm text-slate-700 font-semibold">{u.business_count}</span>
              <div className="flex md:justify-end">
                {u.role === "admin" ? (
                  <span className="text-xs text-slate-400">—</span>
                ) : u.subscription_status === "active" ? (
                  <Button size="sm" variant="secondary" disabled={busyId === u.user_id} onClick={() => act(u, "revoke")}
                    data-testid={`revoke-access-${u.user_id}`}
                    className="bg-white border border-slate-200 text-red-600 hover:bg-red-50">
                    <Prohibit size={15} className="mr-1.5" /> Revoke
                  </Button>
                ) : (
                  <Button size="sm" disabled={busyId === u.user_id} onClick={() => act(u, "grant")}
                    data-testid={`grant-access-${u.user_id}`}
                    className="bg-[#0055FF] hover:bg-[#0044CC] text-white">
                    <Gift size={15} className="mr-1.5" /> Grant free
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        </div>
        )}

        {tab === "inbox" && <ContactInbox onUnreadChange={setInboxUnread} />}
      </div>
    </Layout>
  );
}

const Stat = ({ icon: Icon, label, value, accent = "text-slate-900" }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5">
    <div className="flex items-center gap-2 text-slate-400"><Icon weight="duotone" size={18} />
      <span className="text-xs font-bold uppercase tracking-[0.1em]">{label}</span></div>
    <p className={`font-display text-3xl font-black mt-2 ${accent}`}>{value}</p>
  </div>
);

const ContactInbox = ({ onUnreadChange }) => {
  const [messages, setMessages] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/contacts");
      setMessages(data.messages || []);
      onUnreadChange && onUnreadChange(data.unread || 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setMessages([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openMessage = async (m) => {
    const next = openId === m.contact_id ? null : m.contact_id;
    setOpenId(next);
    setReplyText(m.reply || "");
    if (next && !m.read) {
      try { await api.post(`/admin/contacts/${m.contact_id}/read`); load(); } catch { /* ignore */ }
    }
  };

  const sendReply = async (m) => {
    if (!replyText.trim()) { toast.error("Write a reply first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/contacts/${m.contact_id}/reply`, { reply: replyText });
      toast.success(data.emailed ? "Reply sent by email" : "Reply saved");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (m) => {
    try {
      await api.delete(`/admin/contacts/${m.contact_id}`);
      toast.success("Message deleted");
      if (openId === m.contact_id) setOpenId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const mailto = (m) => {
    const subject = encodeURIComponent(`Re: ${m.subject || "Your message to Local CiteHub"}`);
    const body = encodeURIComponent(`Hi ${m.name},\n\n${replyText}\n\n— Local CiteHub`);
    window.open(`mailto:${m.email}?subject=${subject}&body=${body}`, "_blank");
  };

  return (
    <div data-testid="contact-inbox" className="mt-8">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {messages === null ? (
          <div className="py-16 text-center text-slate-400">Loading…</div>
        ) : messages.length === 0 ? (
          <div className="py-16 text-center text-slate-500" data-testid="inbox-empty">
            <Envelope size={34} className="mx-auto text-slate-300" />
            <p className="mt-3 font-semibold text-slate-700">No messages yet</p>
            <p className="text-sm text-slate-500 mt-1">Messages from your contact form will appear here.</p>
          </div>
        ) : messages.map((m) => (
          <div key={m.contact_id} data-testid={`inbox-row-${m.contact_id}`} className="border-b border-slate-100 last:border-0">
            <button onClick={() => openMessage(m)} data-testid={`inbox-open-${m.contact_id}`}
              className={`w-full text-left px-6 py-4 flex items-start gap-3 hover:bg-slate-50/60 transition-colors ${m.read ? "" : "bg-blue-50/40"}`}>
              <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${m.read ? "bg-slate-200" : "bg-[#0055FF]"}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className={`truncate ${m.read ? "font-medium text-slate-700" : "font-bold text-slate-900"}`}>{m.name}</p>
                  <span className="text-xs text-slate-400 truncate">{m.email}</span>
                  {m.replied_at && <span className="text-[10px] font-bold uppercase text-emerald-600 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5">Replied</span>}
                </div>
                <p className="text-sm text-slate-600 truncate mt-0.5">{m.subject ? <span className="font-semibold">{m.subject}: </span> : null}{m.message}</p>
              </div>
              <span className="text-xs text-slate-400 shrink-0">{m.created_at ? new Date(m.created_at).toLocaleDateString() : ""}</span>
            </button>

            {openId === m.contact_id && (
              <div className="px-6 pb-5 pt-1 bg-slate-50/60" data-testid={`inbox-detail-${m.contact_id}`}>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Message</p>
                  <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap">{m.message}</p>
                </div>
                <div className="mt-4">
                  <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 flex items-center gap-1.5"><ArrowUUpLeft size={14} /> Reply</p>
                  <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={4}
                    data-testid={`inbox-reply-input-${m.contact_id}`} placeholder={`Write a reply to ${m.name}…`} />
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <Button onClick={() => sendReply(m)} disabled={busy} data-testid={`inbox-reply-save-${m.contact_id}`}
                      className="bg-[#0055FF] hover:bg-[#0044CC] text-white">
                      <PaperPlaneTilt size={15} className="mr-1.5" /> {busy ? "Saving…" : "Save reply"}
                    </Button>
                    <Button onClick={() => mailto(m)} variant="secondary" data-testid={`inbox-reply-mailto-${m.contact_id}`}
                      className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50">
                      <Envelope size={15} className="mr-1.5" /> Open in email
                    </Button>
                    <Button onClick={() => del(m)} variant="ghost" data-testid={`inbox-delete-${m.contact_id}`}
                      className="text-slate-500 hover:text-red-600 ml-auto">
                      <Trash size={15} className="mr-1.5" /> Delete
                    </Button>
                  </div>
                  {m.replied_at && <p className="text-xs text-slate-400 mt-2">Last reply saved {new Date(m.replied_at).toLocaleString()}.</p>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const CopyField = ({ label, icon: Icon, value, testid, multiline }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 flex items-center gap-1.5"><Icon size={14} /> {label}</p>
      <div className="flex items-stretch gap-2">
        {multiline ? (
          <pre data-testid={`${testid}-value`} className="flex-1 rounded-md bg-slate-900 text-slate-100 p-3 font-mono text-xs overflow-x-auto whitespace-pre-wrap">{value}</pre>
        ) : (
          <code data-testid={`${testid}-value`} className="flex-1 rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5 font-mono text-xs text-slate-700 truncate flex items-center">{value}</code>
        )}
        <Button onClick={copy} data-testid={`${testid}-copy`} className="bg-[#0055FF] hover:bg-[#0044CC] text-white shrink-0 self-start">
          {copied ? <CheckCircle weight="fill" size={16} className="mr-1.5" /> : <Copy size={16} className="mr-1.5" />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
    </div>
  );
};

const PromoteCard = () => {
  const config = useConfig();
  if (!config) return null;
  const slug = refSlug(config.company_name);
  const referralLink = `${config.app_url}/login?ref=${slug}`;
  const embed = `<a href="${referralLink}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:8px;padding:8px 14px;border-radius:8px;background:#0055FF;color:#fff;font-family:sans-serif;font-size:14px;font-weight:600;text-decoration:none;">
  📍 Get listed everywhere with CiteHub
</a>`;

  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="promote-card">
      <div className="flex items-center gap-2 text-[#0055FF]"><Gift weight="duotone" size={20} />
        <p className="text-xs font-bold uppercase tracking-[0.14em]">Promote CiteHub</p></div>
      <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-2">Share it from {config.company_name}</h2>
      <p className="text-sm text-slate-600 mt-1.5">
        Drop the badge on your website or share your referral link. New signups can enter code <span className="font-mono font-bold text-slate-900">3RDFREE</span> at checkout to get their 3rd month free.
      </p>
      <div className="mt-6 space-y-5">
        <CopyField label="Referral link" icon={LinkSimple} value={referralLink} testid="referral-link" />
        <CopyField label='"Powered by CiteHub" embed (HTML)' icon={Code} value={embed} testid="embed-code" multiline />
      </div>
    </div>
  );
};
